import type { JSONB } from '@risksmart-app/domain/src/types/common.types';
import type { InsertTaxonomyMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertTaxonomyDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

/**
 * Input for the compound taxonomy insert (RSP-6488). The legacy `.graphql`
 * hard-coded these literals because the insert had no form; the tRPC input
 * accepts them as variables so the write is form-ready. `OrgKey` and audit
 * columns are derived server-side from the caller — never sent from here.
 */
export interface InsertTaxonomyInput {
  Description: string;
  Common: JSONB;
  Library: JSONB;
  Rating: JSONB;
  Taxonomy: JSONB;
  InternalAuditRating: JSONB;
  OversightRating: JSONB;
  Locale: string;
  OrgName: string;
}

/**
 * The seed values the legacy `InsertTaxonomy` mutation hard-coded in its
 * `.graphql` (the insert had no form). The tRPC path now sends them as
 * variables; the Apollo path still relies on the document's literals, so this
 * default is only forwarded to the tRPC branch.
 */
const DEFAULT_TAXONOMY_INSERT: InsertTaxonomyInput = {
  Description: 'Description',
  Common: {},
  Library: {},
  Rating: {},
  Taxonomy: {},
  InternalAuditRating: {},
  OversightRating: {},
  Locale: 'en',
  OrgName: '',
};

/**
 * Compound `InsertTaxonomy` mutation (RSP-6488) — a no-input trigger.
 */
export const useInsertTaxonomy = createMutationHook({
  name: 'insertTaxonomy',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.taxonomy.insert.mutationOptions({ onSuccess }),
  mapInput: (_input: void) => DEFAULT_TAXONOMY_INSERT,
  // Mirror the legacy Apollo refetch: the settings tab reads the version
  // history via taxonomyAudit; a new taxonomy adds a version + bumps the
  // org-usage count carried on every audit row.
  invalidate: (trpc) => [
    trpc.frontend.taxonomy.taxonomyAudit.queryKey(),
    trpc.frontend.taxonomy.taxonomyByLocale.queryKey(),
  ],
  mapResponse: (result: { Id: string }): InsertTaxonomyMutation => ({
    insert_taxonomy_one: {
      __typename: 'taxonomy',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertTaxonomyDocument,
  graphqlVariables: () => ({}),
  evictFields: ['taxonomy', 'taxonomy_org', 'taxonomy_audit'],
});
